import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  OAuthProvider,
  User as FirebaseUser,
} from "firebase/auth";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import { authCallback, getProfile, logout } from "../api/generated";
import { setupAuthInterceptor } from "../api/interceptors";
import { auth } from "../config/firebase";
import { authConfig } from "../config/auth";
import {
  AuthCallbackResponse,
  UserProfileResponse,
  UserRole,
} from "../api/generated.schemas";

interface AuthContextValue {
  user: UserProfileResponse | null;
  sessionExpiresAt: Date | null;
  loading: boolean;
  error: Error | null;
  signInWithMicrosoft: () => Promise<void>;
  signInWithEmailPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateSessionExpiry: (expiresAt: Date) => void;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfileResponse | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const interceptorSetup = useRef(false);

  // Clear auth state (used by interceptor on 401)
  const clearAuthState = useCallback(() => {
    setUser(null);
    setSessionExpiresAt(null);
  }, []);

  // Set up 401 interceptor on mount
  useEffect(() => {
    if (!interceptorSetup.current) {
      setupAuthInterceptor(clearAuthState);
      interceptorSetup.current = true;
    }
  }, [clearAuthState]);

  const sendTokenToBackend = useCallback(
    async (
      firebaseUser: FirebaseUser,
      microsoftAccessToken?: string
    ): Promise<UserProfileResponse> => {
      async function post(forceRefresh: boolean): Promise<UserProfileResponse> {
        const token = await firebaseUser.getIdToken(forceRefresh);

        // The orval mutator returns the response body directly
        const data = (await authCallback({
          firebase_token: token,
          microsoft_access_token: microsoftAccessToken,
        })) as unknown as AuthCallbackResponse;

        // If token refresh is required (role claims were updated), retry with fresh token
        if (data.token_refresh_required) {
          return post(true);
        }

        // Store session expiry time
        if (data.session_expires_at) {
          setSessionExpiresAt(new Date(data.session_expires_at));
        }

        return data.user;
      }

      return post(false);
    },
    []
  );

  const signInWithMicrosoft = useCallback(async () => {
    if (!authConfig.enableMicrosoft) {
      throw new Error("Microsoft authentication is not enabled in this environment");
    }

    try {
      setError(null);
      const provider = new OAuthProvider("microsoft.com");
      // Request user profile and email scopes (required for authentication)
      provider.addScope("openid");
      provider.addScope("profile");
      provider.addScope("email");
      // Request calendar access for Outlook integration
      provider.addScope("Calendars.ReadWrite");
      provider.addScope("offline_access");

      const result = await signInWithPopup(auth, provider);

      // Extract Microsoft access token for Graph API calls
      const credential = OAuthProvider.credentialFromResult(result);
      const microsoftAccessToken = credential?.accessToken;

      const userProfile = await sendTokenToBackend(result.user, microsoftAccessToken);
      setUser(userProfile);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [sendTokenToBackend]);

  const signInWithEmailPassword = useCallback(
    async (email: string, password: string) => {
      if (!authConfig.enableEmailPassword) {
        throw new Error("Email/password authentication is not enabled in this environment");
      }

      try {
        setError(null);
        const result = await signInWithEmailAndPassword(auth, email, password);
        const userProfile = await sendTokenToBackend(result.user);
        setUser(userProfile);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [sendTokenToBackend]
  );

  const signOut = useCallback(async () => {
    try {
      setError(null);
      // Attempt server-side logout, but don't fail if session is already invalid
      try {
        await logout();
      } catch (logoutError) {
        // 401 means session is already invalid - proceed with local cleanup
        const isUnauthorized =
          logoutError instanceof AxiosError && logoutError.response?.status === 401;
        if (!isUnauthorized) {
          // Log unexpected errors but don't block logout
          Sentry.captureException(logoutError);
        }
      }
      await firebaseSignOut(auth);
      setUser(null);
      setSessionExpiresAt(null);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setError(null);
      // The orval mutator returns the response body directly
      const profile = (await getProfile()) as unknown as UserProfileResponse;
      setUser(profile);
    } catch {
      setUser(null);
    }
  }, []);

  const hasRole = useCallback(
    (roles: UserRole | UserRole[]): boolean => {
      if (!user) return false;
      const roleArray = Array.isArray(roles) ? roles : [roles];

      // Role hierarchy: super_admin > administrator > director > manager > user
      // Higher roles inherit all permissions of lower roles
      const roleHierarchy: Record<UserRole, UserRole[]> = {
        [UserRole.user]: [UserRole.user],
        [UserRole.manager]: [UserRole.user, UserRole.manager],
        [UserRole.director]: [UserRole.user, UserRole.manager, UserRole.director],
        [UserRole.administrator]: [
          UserRole.user,
          UserRole.manager,
          UserRole.director,
          UserRole.administrator,
        ],
        [UserRole.super_admin]: [
          UserRole.user,
          UserRole.manager,
          UserRole.director,
          UserRole.administrator,
          UserRole.super_admin,
        ],
      };

      const userRoles = roleHierarchy[user.role] || [user.role];
      return roleArray.some((role) => userRoles.includes(role));
    },
    [user]
  );

  const updateSessionExpiry = useCallback((expiresAt: Date) => {
    setSessionExpiresAt(expiresAt);
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  // Set Sentry user context when user changes
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.uid,
        role: user.role,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Note: The old Firebase token refresh interval has been removed.
  // With session cookies, the session remains valid for its full duration
  // without needing to refresh the Firebase ID token on the client.

  // Memoize the context value so consumers only re-render when auth state
  // actually changes (all functions above are useCallback-stable).
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      sessionExpiresAt,
      loading,
      error,
      signInWithMicrosoft,
      signInWithEmailPassword,
      signOut,
      refreshUser,
      updateSessionExpiry,
      hasRole,
    }),
    [
      user,
      sessionExpiresAt,
      loading,
      error,
      signInWithMicrosoft,
      signInWithEmailPassword,
      signOut,
      refreshUser,
      updateSessionExpiry,
      hasRole,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
